import { 
  Grid, 
  Paper, 
  Typography, 
  Container,
  Box,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import './App.css';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2, 0),
  },
  container: {
    maxWidth: '1200px',
  },
  paper: {
    padding: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
    borderRadius: theme.spacing(2),
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  },
  logo: {
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
    marginBottom: theme.spacing(4),
    display: 'block',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  productImage: {
    width: '100%',
    maxWidth: '700px',
    height: 'auto',
    margin: `${theme.spacing(3)}px auto`,
    display: 'block',
    borderRadius: theme.spacing(1),
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  subtitle: {
    fontWeight: 600,
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(2)}px 0`,
    color: theme.palette.secondary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
      margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
    },
  },
  bodyText: {
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  benefitCard: {
    margin: theme.spacing(1, 0),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  benefitTitle: {
    fontWeight: 600,
    display: 'inline',
    color: theme.palette.primary.dark,
  },
  contactSection: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    margin: theme.spacing(3, 0),
  },
  tagline: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1.2rem',
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.secondary.light,
    borderRadius: theme.spacing(1),
    color: theme.palette.secondary.dark,
  },
}));

function App() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box className={classes.root}>
      <Container className={classes.container}>
        <Paper elevation={0} className={classes.paper}>
          <img 
            src="./BadHareLogo.png" 
            alt="Bad Hare Gaming Data Cradle" 
            className={classes.logo}
          />

          <Typography variant={isMobile ? 'h4' : 'h3'} className={classes.title}>
            Introducing the Data Cradle by Bad Hare Gaming
          </Typography>

          <Typography variant={isMobile ? 'h6' : 'h5'} className={classes.subtitle}>
            Revolutionizing Casino Analytics
          </Typography>

          <Typography className={classes.bodyText}>
            Improve your casino operations with the latest innovation in gaming technology - The Data Cradle. 
            Harness the power of Deep Neural Network (DNN) to turn your blackjack tables into a goldmine of insights.
          </Typography>

          <Divider style={{ margin: '32px 0' }} />

          <Typography variant={isMobile ? 'h6' : 'h5'} className={classes.subtitle}>
            What is The Data Cradle?
          </Typography>

          <img 
            src="./BadHareGamingDataCradle.jpg" 
            alt="Bad Hare Gaming Data Cradle Product" 
            className={classes.productImage}
          />

          <Typography className={classes.bodyText}>
            The Data Cradle is a revolutionary product that leverages advanced DNN technology to analyze blackjack tables 
            in real-time. Simply place your standard card shoe into the Data Cradle and watch as it processes the cards 
            coming out, transmitting all the data to a central system.
          </Typography>

          <Typography className={classes.bodyText}>
            This allows for real-time data collection, analytics, and performance optimization, turning your tables into 
            a comprehensive, dynamic system that provides you with the strategic information you need when you need it.
          </Typography>

          <Typography variant={isMobile ? 'h6' : 'h5'} className={classes.subtitle}>
            Benefits
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card className={classes.benefitCard} elevation={0}>
                <CardContent>
                  <Typography className={classes.benefitTitle}>Operational Efficiency:</Typography>
                  <Typography display="inline">
                    {' '}Identify slow dealers and streamline your operations, ensuring maximum table turnover.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className={classes.benefitCard} elevation={0}>
                <CardContent>
                  <Typography className={classes.benefitTitle}>Security:</Typography>
                  <Typography display="inline">
                    {' '}Minimize risks with the ability to identify tables susceptible to card counters.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className={classes.benefitCard} elevation={0}>
                <CardContent>
                  <Typography className={classes.benefitTitle}>Post-Analysis:</Typography>
                  <Typography display="inline">
                    {' '}Gain valuable insights for continuous improvement through post-game analysis.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className={classes.benefitCard} elevation={0}>
                <CardContent>
                  <Typography className={classes.benefitTitle}>Live Data:</Typography>
                  <Typography display="inline">
                    {' '}Stay ahead with real-time data transmission, allowing you to make swift, data-driven decisions.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant={isMobile ? 'h6' : 'h5'} className={classes.subtitle}>
            Integrate Data Cradle Today
          </Typography>

          <Typography className={classes.bodyText}>
            Join the growing number of casinos around the world harnessing the power of the Data Cradle. 
            Experience the future of casino analytics and transform your gaming operations today.
          </Typography>

          <Paper className={classes.contactSection} elevation={3}>
            <Typography variant="h6" gutterBottom>
              Ready to Get Started?
            </Typography>
            <Typography>
              For more information or to schedule a demo, please reach out to:
            </Typography>
            <Typography variant="h6" style={{ marginTop: 16 }}>
              Rick Lopez
            </Typography>
            <Typography variant="body1">
              Email: rick@rolledupgaming.com
            </Typography>
          </Paper>

          <Typography className={classes.tagline}>
            Data Cradle – Empowering Your Casino Operations with the Power of Data.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default App;
